import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { ContentNode, ContentTitle, EntityGrid, EntityMultiValueList, MainAside, PageHeader, PageHeaderProps, SliceLayoutBoundary, useLang, useVideos } from '@shapeable/ui';
import { ORANGE, PRIMARY_BLUE } from '../../theme';
import { classNames } from '@shapeable/utils';
import { VideoDetailsHeader } from './video-details-header';
import { sortBy, reverse } from 'lodash';
import { Video } from '@shapeable/kelp-forest-alliance-types';

const cls = classNames('video-banner-layout');

// -------- Types -------->

export type VideoBannerLayoutProps = Classable & HasChildren & {
  entity?: Video;
  headerProps?: Partial<PageHeaderProps>;
};

export const VideoBannerLayoutDefaultProps: Omit<VideoBannerLayoutProps, 'entity'> = {
};

// -------- Child Component Props -------->

type ContainerProps = {

}

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    h1.shp--content-title {
      font-size: 1.2em;
      text-transform: none;
      margin-bottom: ${theme.UNIT(2)};
    }
  `,
});

const HeaderStyles = breakpoints({
  base: css`
    
  `,
});

const BannerDetailsStyles = breakpoints({
  base: css`
    
  `,
});

const BodyStyles = breakpoints({
  base: css`
    width: 100%;
    margin-bottom: ${theme.UNIT(6)};
  `,
});

const MainAsideStyles = breakpoints({
  base: css`
    padding: ${theme.UNIT(10)} 0;
  `,
  tablet: css`
  `,
});

const AsideStyles = breakpoints({
  base: css`
  `,
  desktop: css`
    padding-left: 0;
    padding-right: 0;
  `,
});

const AsideItemStyles = breakpoints({
  base: css`
    margin-bottom: ${theme.UNIT(4)};
  `,
});

const MainTitleStyles = breakpoints({
  base: css`
    margin-bottom: ${theme.UNIT(4)};
  `,
});

const DescriptionStyles = breakpoints({
  base: css`
    ${theme.H_DOT_BG_END()};
    padding-bottom: ${theme.UNIT(4)};
    margin-bottom: ${theme.UNIT(4)};
  `,
});

const RelatedContentStyles = breakpoints({
  base: css`
    padding: ${theme.UNIT(6)} ${theme.UNIT(0)} ${theme.UNIT(7)};
    ${theme.H_DOT_BG_END()};
    margin-bottom: ${theme.UNIT(4)};
  `,
});

const AuthorsStyles = breakpoints({
  base: css`
    ${AsideItemStyles};
  `,
});

const PeopleStyles = breakpoints({
  base: css`
    ${AsideItemStyles};
  `,
});

const RegionsStyles = breakpoints({
  base: css`
    ${AsideItemStyles};
  `,
});

const TraditionalLandsStyles = breakpoints({
  base: css`
    ${AsideItemStyles};
  `,
});

// -------- Components -------->

const My = {
  Container: styled.div<ContainerProps>`${ContainerStyles}`,
    Header: styled(PageHeader).attrs(cls.attr('header'))`${HeaderStyles}`,
      BannerDetails: styled(VideoDetailsHeader)`${BannerDetailsStyles}`,
    Body: styled.div`${BodyStyles}`,
      MainAside: styled(MainAside)`${MainAsideStyles}`,
        MainTitle: styled(ContentTitle)`${MainTitleStyles}`,
        Description: styled(ContentNode)`${DescriptionStyles}`,
        RelatedContent: styled(EntityGrid)`${RelatedContentStyles}`,
      Aside: styled.div`${AsideStyles}`,
        Authors: styled(EntityMultiValueList)`${AuthorsStyles}`,
        People: styled(EntityMultiValueList)`${PeopleStyles}`,
        Regions: styled(EntityMultiValueList)`${RegionsStyles}`,
        TraditionalLands: styled(EntityMultiValueList)`${TraditionalLandsStyles}`,
};

export const VideoBannerLayout: Shapeable.FC<VideoBannerLayoutProps> = (props) => {
  const { className, children, headerProps, entity } = props;

  const { description, authors = [], people = [], regions = [], feedEntries = [], posts = [] } = entity;

  const videos = useVideos().filter(video => (!!entity?.type?.slug && video?.type?.slug === entity?.type?.slug) && (video?.id !== entity?.id));
  const relatedContent = sortBy(
    [ ...feedEntries, ...posts, ...videos ],
    (item) => item.published ? new Date(item.published).getTime() : 0,
  ).reverse();


  const hasRelatedContent = !!relatedContent.length;
  const hasDescription = !!description && !!description.text;
  const hasAuthors = !!authors.length;
  const hasPeople = !!people.length;
  const hasRegions = !!regions.length;

  const t = useLang();

  return (
    <My.Container className={cls.name(className)}>
      <My.Header useEntityColors entity={entity} {...headerProps} variant='overlay' backgroundColor={PRIMARY_BLUE} fadeColor='#0000000'>
        <My.BannerDetails entity={entity} color='#FFFFFF' badgeColor={ORANGE} />
      </My.Header>

      <SliceLayoutBoundary>
        <My.Body>
          <My.MainAside
            aside={
              <My.Aside>
                {hasAuthors && <My.Authors headerLabel={t('Authors:')} items={authors} />}
                {hasPeople && <My.People headerLabel={t('People:')} items={people} />}
                {hasRegions && <My.Regions headerLabel={t('Regions:')} items={regions} />}
              </My.Aside>
            }>
            {
              hasDescription && (
                <>
                  <My.MainTitle>{t('Description:')}</My.MainTitle>
                  <My.Description entity={description} />
                </>
              )
            }
            {
              hasRelatedContent && (
                <>
                  <My.MainTitle>{t('Related Content:')}</My.MainTitle>
                  <My.RelatedContent spacing={2} maxColumns={3} items={relatedContent.slice(0,3)} />
                </>
              )
            }
          </My.MainAside>
        </My.Body>
      </SliceLayoutBoundary>
      {children}
    </My.Container>
  );
};

VideoBannerLayout.defaultProps = VideoBannerLayoutDefaultProps;
VideoBannerLayout.cls = cls;
